import React, { useContext } from "react"
import { AgreementContext } from "../../../../context/AgreementContext"

const PaymentTerms = () => {
  const { createAppLink } = useContext(AgreementContext)
  return (
    <>
      <p>
        <strong>
          If you reside in, or if the organization you are acting for is
          established in, the United States, the arbitration agreement and class
          actions waiver in Section 15 apply to you. Please read them carefully.
        </strong>
      </p>
      <p>Last Updated: March 15, 2023</p>
      <p>
        These Payments Terms of Use are a binding legal agreement between you
        and Teache that governs the Payment Services (defined below) conducted
        through or in connection with the Teache Platform.
      </p>
      <p>
        Teache provides payment services to Members publishing, offering, and
        booking Classes and other current and future services provided via the
        Teache Platform. These payment services may include (if available) the
        following (collectively, &ldquo;<strong>Payment Services</strong>
        &rdquo;):
      </p>
      <ul>
        <li>
          Collecting payments from Users (&ldquo;<strong>Paying</strong>&rdquo;)
          by charging the payment method associated with their Teache accounts,
          such as a credit card, debit card, bank account, or PayPal account
          (&ldquo;<strong>Payment Method</strong>&rdquo;)
        </li>
        <li>
          Effecting payments to Instructors (&ldquo;<strong>Payout</strong>
          &rdquo;) to a financial instrument associated with their Teache
          account, such as a PayPal account, bank account, prepaid card, or
          debit card (&ldquo;<strong>Payout Method</strong>&rdquo;)
        </li>
        <li>
          Effecting payments to a third-party Payout Method designated by an
          Instructor
        </li>
        <li>Collection and payment of charitable donations</li>
        <li>Payment collection services; and</li>
        <li>
          Other payment-related services in connection with Instructor Services.
        </li>
      </ul>
      <p>
        In order to use the Payment Services, you must be at least 18 years old,
        have a Teache account in good standing in accordance with the
        Teache&nbsp;Terms of Use&nbsp;(&ldquo;Terms&rdquo;), and keep your
        payment and personal information accurate and complete.
      </p>
      <p>
        Payment Services will be provided by Teache, Inc.
        <br /> <br /> The&nbsp;
        <a href={createAppLink("/policies/terms")}>Terms</a>&nbsp;separately
        govern your use of the Teache Platform. If you see an undefined term in
        these Payment Terms, it has the same definition as it does in the{" "}
        <a href={createAppLink("/policies/terms")}>Terms</a>.
      </p>
      <ol>
        <li>
          <strong> Your use of the Payment Services</strong>
        </li>
        <p>
          <strong>1.1 Teache Payment Services</strong>. By using the Payments
          Services, you agree to comply with these Payment Terms. Teache may
          temporarily limit or suspend your access to or use of the Payment
          Services or its features to carry out maintenance measures that ensure
          the proper functioning of the Payment Services. Teache may improve,
          enhance, and modify the Payment Services and introduce new Payment
          Services occasionally. Teache will notify Members of any changes to
          the Payment Services, unless such changes do not materially increase
          the Members&rsquo; contractual obligations or decrease the
          Members&rsquo; rights under these Payments Terms.
        </p>
        <p>
          <strong>1.2 Third-Party Services</strong>. The Payment Services may
          contain links to third-party websites or resources (&ldquo;
          <strong>Third-Party Services</strong>&rdquo;). Such Third-Party
          Services are subject to different terms of service and privacy
          policies, and Members should review them. Teache is not responsible or
          liable for the use of such Third-Party Services. Links to any
          Third-Party Services are not endorsed by Teache of those Third-Party
          Services.
        </p>
        <p>
          <strong>1.3 Your Teache Account</strong>. Teache may enable features
          that allow you to authorize other Members or third parties to take
          certain actions that affect your Teache account. You may authorize a
          third party to use your Teache account, if the feature is enabled for
          your Teache account. You acknowledge and agree that anyone you
          authorize to use your Teache account may use the Payment Services on
          your behalf and that you will be responsible for any payments made by
          such person(s).
        </p>
        <li>
          <strong> User Terms</strong>
        </li>
        <p>
          <strong>2.1 Adding a Payment Method</strong>. When you add a Payment
          Method to your Teache account, you will be asked to provide billing
          information, such as a name, billing address, and financial instrument
          information, either to Teache or to its third-party payment
          processor(s). You authorize Teache and its payment service providers
          to collect and store your Payment Method information.
        </p>
        <p>
          <strong>2.2 Payment Method Verification</strong>. When you add or use
          a new Payment Method, Teache may verify the Payment Method by (i)
          authorizing your Payment Method for one or two nominal amounts via a
          payment service provider and asking you to confirm those amounts or
          (ii) requiring you to upload a billing statement. We may, and we
          retain the right to, initiate refunds of these amounts from your
          Payment Method. When you add a Payment Method during checkout, we will
          automatically save and add that Payment Method to your Teache account,
          so it can be used for future transactions. You can remove the Payment
          Method from your Teache account, if it is not associated with an
          active or future reservation.
        </p>
        <p>
          <strong>2.3 Payment Authorization</strong>. You allow Teache to charge
          your Payment Method (including charging more than one payment method),
          either directly or indirectly, for all fees due (including any
          applicable taxes) in connection with your Teache account.
        </p>
        <p>
          <strong>2.4 Automatic Update of Payment Method</strong>. If your
          Payment Method&rsquo;s account information (e.g., account number,
          routing number, expiration date) changes due to re-issuance or
          otherwise, we may acquire that information from our financial services
          partners or your bank and automatically update your Payment Method on
          file.
        </p>
        <p>
          <strong>2.5 Timing of Payment</strong>. Teache generally charges the
          Total Price due after the Instructor accepts your booking request. In
          some cases Teache will collect the Total Price due at the time of your
          booking request. Teache may offer alternative options for the timing
          and manner of payment. Any additional fees for using those alternative
          payment options will be displayed via the Teache Platform and will be
          included in the Total Price; you agree to pay such fees by selecting
          the payment option. Additional terms and conditions may apply for
          using an alternative payment option. If Teache cannot collect the
          Total Price due as scheduled, Teache will collect the Total Price due
          later in accordance with Section 5.3. Once the payment for your
          requested booking is successfully completed, you will receive a
          confirmation email.
        </p>
        <p>
          <strong>2.6 Currency</strong>. Teache will process each transaction in
          your selected currency via the Teache Platform. The currencies
          available to make payments may be limited for regulatory or
          operational reasons based on factors, such as your selected Payment
          Method, your country of residence, and/or your Teache contracting
          entity/entities. Any such limitations will be communicated via the
          Teache Platform, and, if necessary, you will be prompted to select a
          different currency or Payment Method. Please note that if your Teache
          contracting entity&rsquo;s location is different from the country of
          your Payment Method or if your selected currency is different from
          your Payment Method's billing currency, your payment may be processed
          outside of your country of residence. As a result, certain fees may
          apply, and the amount on your card statement may differ from the
          amount shown at checkout. For example, suppose you make a booking
          using a US-issued card but select Euros as your currency. In that
          case, your payment may be processed outside the U.S., and banks and
          credit card companies may impose international transaction fees and
          foreign exchange fees. In addition, if you select to pay with a
          currency that is different from your Payment Method's billing
          currency, your bank or credit card company may convert the payment
          amount to your billing currency associated with your Payment Method
          based on an exchange rate and fee amount determined solely by your
          bank. Teache is not responsible for any such fees and disclaims all
          liability in this regard. Please contact your bank or credit card
          company, if you have any questions about these fees or the applicable
          exchange rate.
        </p>
        <p>
          <strong>2.7 Booking Request Status</strong>. Suppose that a requested
          booking is declined either because the Instructor does not accept it
          or because you cancel the booking request before it is accepted by the
          Instructor. In that case, any amounts collected by Teache will be
          refunded to you, and any pre-authorization of your Payment Method will
          be released (if applicable). The time required to receive the refund
          or for the pre-authorization to be released will vary, based on the
          Payment Method and any applicable payment system (e.g., Visa,
          MasterCard, etc.) rules.
        </p>
        <p>
          <strong>2.8 Payment Restrictions.</strong>&nbsp;Teache reserves the
          right to decline or limit payments that we believe (i) may violate
          Teache&rsquo; risk management policies or procedures, (ii) may violate
          these Payments Terms or the&nbsp;
          <a href={createAppLink("/policies/terms")}>Terms</a>, (iii) are
          unauthorized, fraudulent or illegal, or (iv) expose you, Teache,
          Teache, or others to risks unacceptable to Teache.
        </p>
        <p>
          <strong>2.9 Payment Service Providers.</strong>&nbsp;Payment Methods
          may involve the use of third-party payment service providers. These
          payment service providers may charge you additional fees when
          processing payments in connection with the Payment Services. Teache is
          not responsible for any such fees and disclaims all liability in this
          regard. Your Payment Method may also be subject to additional terms of
          use. Please review them before using your Payment Method.
        </p>
        <p>
          <strong>2.10 Your Payment Method, Your Responsibility</strong>. Teache
          is not responsible for any loss suffered by you as a result of
          incorrect Payment Method information provided by you.
        </p>
        <p>
          <strong>2.11 Different Ways to Pay</strong>
        </p>
        <p>
          <strong>2.11.1 Payment Plan</strong>
        </p>
        <p>
          2.11.1.1 Teache may make available to Users the option to pay a
          portion of a Class booking&rsquo;s Total Price at the time of booking
          and pay the remainder of the Total Price at a later time before the
          start of the Class (&ldquo;<strong>Payment Plan</strong>&rdquo;).
          Availability of this option may depend on the Listing and/or Payment
          Method.
        </p>
        <p>
          2.11.1.2 If you choose a Payment Plan, the Teache Platform will notify
          you, during checkout, of the amount, currency, and schedule of each
          payment due. On the following payment&rsquo;s due date, Teache will
          automatically charge the original Payment Method you used to make the
          booking.
        </p>
        <p>
          2.11.1.3 If you make a modification to a booking made with a Payment
          Plan, Teache will notify you of the revised payment schedule, as
          applicable. If the modification increases your Total Price, you may be
          required to make an additional partial payment of the new Total Price
          at the time of the modification.
        </p>
        <p>
          2.11.1.4 You agree that by selecting a Payment Plan, you may not be
          able to pay for the booking with a different Payment Method or
          pursuant to a different payment schedule.
        </p>
        <p>
          2.11.1.5 If Teache cannot collect your payment, Teache will notify you
          of such declined payment and require you to complete the payment using
          an alternative Payment Method within 72 hours of the notice. If you
          fail to complete the payment, you authorize Teache to cancel the
          booking on your behalf. If the booking is canceled, you will be
          refunded based on the Instructor&rsquo;s cancellation policy. You
          acknowledge that you may incur fees for cancellations pursuant to the
          Instructor&rsquo;s cancellation policy.
        </p>
        <p>
          <strong>2.11.2 Recurring Payments</strong>
        </p>
        <p>
          2.11.2.1 For certain bookings, Teache may require a User to make
          recurring, incremental payments toward the Total Price owed (&ldquo;
          <strong>Recurring Payments</strong>&rdquo;). More information on
          Recurring Payments (including the amount and the frequency of
          payments) will be made available via the Teache Platform, if
          applicable to a booking.
        </p>
        <p>
          2.11.2.2 If Recurring Payments apply to a confirmed booking, then the
          User authorizes Teache to collect the Total Price due.
        </p>
        <p>
          2.11.2.3 Users may stop a Recurring Payment by notifying Teache orally
          or in writing at least three (3) business days before the scheduled
          payment date. Teache may require you to give written confirmation of a
          stop-payment order within fourteen (14) days of an oral notification.
          If you fail to provide written confirmation within those fourteen (14)
          days, Teache is not obligated to honor your request to stop any future
          Recurring Payments. If you have any questions regarding your Recurring
          Payments, please get in touch with hello@teache.co.
        </p>
        <li>
          <strong> Instructor Terms</strong>
        </li>
        <p>
          <strong>3.1 Payment Collection</strong>. Teache generally collects the
          Total Price of a booking when a User&rsquo;s booking request is
          accepted by the Instructor, unless noted otherwise.
        </p>
        <p>
          <strong>3.2 Adding a Payout Method</strong>. To receive a Payout, you
          must have a valid Payout Method linked to your Teache account. When
          you add a Payout Method to your Teache account, you will be asked to
          provide billing information, such as name, government identification,
          tax ID, billing address, and financial instrument information either
          to Teache or to its third-party payment processor(s). Depending on the
          Payout Method selected, additional information may be required, such
          as a residential address, name on the account, account type, routing
          number, account number, email address, payout currency, identification
          number, and account information associated with a particular payment
          processor. You authorize Teache to collect and store your billing
          information and financial instrument information. Teache may also
          share your information with governmental authorities, as required by
          applicable law.
        </p>
        <p>
          <strong>3.3 Timing of Payout</strong>
        </p>
        <p>
          3.3.1 Subject to and conditional upon successful receipt of the
          payments from User, Teache will generally initiate Payouts to your
          selected Payout Method: (i) for Classes, 48 hours after the
          User&rsquo;s scheduled class end time; (ii) for all other Instructor
          Services, at the time specified via the Teache Platform. For multiple
          class bookings, Payouts are to be processed separately for each class,
          48 hours after each of the User&rsquo;s scheduled Class&rsquo;s end
          time.
        </p>
        <p>
          <strong>3.4 Payout</strong>. Your Payout for a class will be the Total
          Price less applicable fees, such as Teache's service fees and
          applicable taxes. In the event of cancellation of a confirmed booking,
          Teache will remit the amount you are due (if any), as provided in
          the&nbsp;<a href={createAppLink("/policies/terms")}>Terms</a>&nbsp;and
          applicable cancellation policy.
        </p>
        <p>
          <strong>3.5 Payout Restrictions</strong>. Teache may temporarily place
          a hold, suspend, or cancel any Payout to prevent unlawful activity or
          fraud, risk assessment, security, or the completing of an
          investigation or if we are unable to verify your identity.
          Furthermore, Teache may temporarily place a hold on, suspend, or delay
          initiating or processing any Payout due to you under the&nbsp;
          <a href={createAppLink("/policies/terms")}>Terms</a>&nbsp;due to
          high-volume Class cancellations or modifications arising from a Force
          Majeure Event (as defined below).
        </p>
        <p>
          <strong>3.6 Currency Conversion</strong>. Teache will remit your
          Payouts in the currency that you select via the Teache Platform. The
          currencies available may be limited for regulatory or operational
          reasons based on factors, such as your selected Payout Method, your
          country of residence, and/or your Teache contracting entity(ies). Any
          such limitations will be communicated via the Teache Platform, and you
          will be prompted to select a different currency or Payout Method. Note
          that payment service providers may impose transaction, currency
          conversion, or other fees based on the currency or Payout Method you
          select. Teache is not responsible for any such fees and disclaims all
          liability in this regard.
        </p>
        <p>
          <strong>3.7 Limits on Payouts</strong>. For compliance or operational
          reasons, Teache may limit a Payout amount. If you are due an amount
          above that limit, Teache may make a series of Payouts (potentially
          over multiple days) to provide your full Payout amount.
        </p>
        <p>
          <strong>3.8 Payment Service Providers</strong>. Payout Methods may
          involve the use of third-party payment service providers. These
          payment service providers may charge you additional fees when
          processing Payouts in connection with the Payment Services (including
          deducting charges from the Payout amount). Teache is not responsible
          for any such fees and disclaims all liability in this regard. Your
          Payout Method may also be subject to additional terms of use. Please
          review them before using your Payout Method.
        </p>
        <p>
          <strong>3.9 Handling of Funds</strong>. Teache may combine amounts
          that it collects from Users and invest them as permitted under
          applicable laws. Teache will retain any interest it earns on those
          investments.
        </p>
        <p>
          <strong>3.10 Your Payout Method, Your Responsibility</strong>. Teache
          is not responsible for any loss suffered by you as a result of
          incorrect Payout Method information provided by you.
        </p>
        <li>
          <strong>
            {" "}
            Appointment of Teache as Limited Payment Collection Agent
          </strong>
        </li>
        <p>
          4.1 Each Instructor, including each Instructor Team member, hereby
          appoints Teache as the Instructor&rsquo;s payment collection agent
          solely for the limited purpose of accepting and processing funds from
          Users purchasing Instructor Services on the Instructor&rsquo;s behalf.
        </p>
        <p>
          4.2 Each Instructor, including each Instructor Team member, agrees
          that payment(s) made by a User through Teache shall be considered the
          same as a payment made directly to the Instructor and that the
          Instructor will provide the Instructor Service booked by the User in
          the agreed-upon manner as if the Instructor has received the payment
          directly from the User. Each Instructor agrees that Teache may refund
          the User in accordance with the&nbsp;
          <a href={createAppLink("/policies/terms")}>Terms</a>. Each Instructor
          understands that Teache's obligation to pay the Instructor is subject
          to and conditional upon the successful receipt of the associated
          payments from the User. Teache guarantees payments to Instructor(s)
          only for the amounts Teache has successfully received from Users per
          these Payments Terms. In accepting an appointment as the limited
          payment collection agent of the Instructor, Teache assumes no
          liability for any acts or omissions of the Instructor.
        </p>
        <p>
          4.3 Each User acknowledges and agrees that, although Teache is not a
          party to the agreement between you and the Instructor(s), including
          each Instructor Team member, Teache acts as each Instructor&rsquo;s
          payment collection agent for the limited purpose of accepting payments
          from you on behalf of the Instructor(s). Upon a User&rsquo;s payment
          of the funds to Teache, the User&rsquo;s payment obligation to the
          Instructor(s) for the agreed-upon amount is extinguished, and Teache
          is responsible for remitting the funds successfully received by Teache
          to the Instructor(s) in the manner described in these Payments Terms.
          In the event that Teache does not remit any such amounts, the
          Instructor(s) will have recourse only against Teache and not the User
          directly.
        </p>
        <li>
          <strong> General Terms</strong>
        </li>
        <p>
          <strong>5.1 Fees</strong>. Teache may charge fees for the use of
          certain Payment Services, and any applicable fees will be disclosed to
          you in the&nbsp;<a href={createAppLink("/policies/terms")}>Terms</a>
          &nbsp;or via the Teache Platform.
        </p>
        <p>
          <strong>5.2 Payment Authorizations</strong>. You authorize Teache to
          collect from you amounts due pursuant to these Payment Terms and/or
          the&nbsp;<a href={createAppLink("/policies/terms")}>Terms</a>&nbsp;by
          either (i) charging the Payment Method associated with the relevant
          Class booking or any other Payment Method on file that you authorize
          in your Teache account (unless you have previously removed the
          authorization to charge such Payment Method(s)), or (ii) by
          withholding the amount from your future Payout. Specifically, you
          authorize Teache to collect from you:
        </p>
        <ul>
          <li>
            Any amount due to Teache (e.g., as a result of your bookings,
            Booking Modifications, cancellations, or other actions by a User of
            or Instructor on the Teache Platform), including reimbursement for
            costs prepaid by Teache on your behalf. Any funds collected by
            Teache will offset the amount you owe to Teache, and extinguish your
            obligation to Teache.
          </li>
          <li>
            Any amount due to an Instructor from a User which Teache collects as
            the Instructor&rsquo;s payment collection agent, as further
            established in Section 4 above.
          </li>
          <li>
            Taxes, where applicable and as established in the&nbsp;
            <a href={createAppLink("/policies/terms")}>Terms</a>.
          </li>
          <li>
            Any amount you pay through the Resolution Center in connection with
            your Teache account.
          </li>
          <li>
            Any service fees or cancellation fees imposed pursuant to the&nbsp;
            <a href={createAppLink("/policies/terms")}>Terms</a>&nbsp;(e.g., if,
            as an Instructor, you cancel a confirmed booking).
          </li>
          <li>
            Any amounts already paid to you as an Instructor despite a User
            canceling a confirmed booking or Teache deciding that it is
            necessary to cancel a booking in accordance with the&nbsp;
            <a href={createAppLink("/policies/terms")}>Terms,</a>&nbsp;
            <a href={createAppLink("/policies/user-refund")}>
              User Refund Policy
            </a>
            , or other applicable cancellation policy. You agree that if you
            have already been paid, Teache will be entitled to recover the
            amount of any such User refund from you, including by subtracting
            such refund amount from any future Payouts due to you.
          </li>
        </ul>
        <p>
          In addition to any amounts due as outlined above, if delinquent
          amounts or chargebacks are associated with your Payment Method, you
          may be charged fees that are incidental to Teache's collection of
          these delinquent amounts and chargebacks. Such fees or charges may
          include collection fees, convenience fees, or other third-party
          charges.
        </p>
        <p>
          <strong>5.3 Collections</strong>
        </p>
        <p>
          5.3.1 If Teache cannot collect any amounts you owe under these
          Payments Terms, Teache may engage in collection efforts to recover
          such amounts from you.
        </p>
        <p>
          5.3.2 Teache will deem any owed amounts overdue when (a) for
          authorized charges, one hundred and twenty (120) days have elapsed
          after Teache first attempts to charge your Payment Method or the
          associated services have been provided, whichever is later; or (b) for
          withholdings from an Instructor&rsquo;s future Payouts, two hundred
          and seventy (270) days have elapsed after the adjustment is made to
          the Instructor&rsquo;s Teache account or the associated services have
          been provided, whichever is later.
        </p>
        <p>
          5.3.3 Teache will deem any overdue amounts not collected to be in
          default when three hundred and sixty-five (365) days have elapsed: (a)
          for authorized charges after Teache first attempts to charge your
          Payment Method or the associated services have been provided,
          whichever is later; and, (b) for withholdings from an
          Instructor&rsquo;s future Payouts, after the adjustment is made to the
          Instructor&rsquo;s Teache account or the associated services have been
          provided, whichever is later.
        </p>
        <p>
          5.3.4 You hereby explicitly agree that all communications in relation
          to amounts owed will be made by electronic mail or by phone, as
          provided to Teache and/or Teache by you. Such communications may be
          made by Teache or by anyone on their behalf, including but not limited
          to a third-party collection agent.
        </p>
        <p>
          <strong>5.4 Payment Processing Errors</strong>
        </p>
        <p>
          5.4.1 Teache will take the necessary steps to rectify any payment
          processing errors that we become aware of. These steps may include
          crediting or debiting (as appropriate) the original Payout Method or
          Payment Method used or selected by you, so that you receive or pay the
          correct amount. This may be performed by Teache or a third party, such
          as your financial institution. We may also take steps to recover funds
          sent to you in error (including but not limited to an event of
          duplicate payments made to you due to a processing error) by reducing,
          offsetting and/or debiting the amount of such funds from any future
          Payouts owed to you.
        </p>
        <p>
          5.4.2 To the extent you receive any funds in error, you agree to
          return such funds to Teache immediately.
        </p>
        <p>
          <strong>5.5 Refunds</strong>
        </p>
        <p>
          5.5.1 Any refunds or credits due to a Member pursuant to the&nbsp;
          <a href={createAppLink("/policies/terms")}>Terms</a>,&nbsp;
          <a href={createAppLink("/policies/user-refund")}>
            User Refund Policy
          </a>
          , and&nbsp;
          <a href={createAppLink("/policies/user-cancellation")}>
            User Cancellation Policy
          </a>
          &nbsp;will be initiated and remitted by Teache in accordance with
          these Payments Terms.
        </p>
        <p>
          5.5.2 Subject to this Section 5.5.1, Teache will process refunds
          immediately. However, the timing to receive any refund will depend on
          the Payment Method and any applicable payment system (e.g., Visa,
          Mastercard, etc.) rules. In the event of a Force Majeure Event that
          may affect the processing and settlement of refunds, Teache will
          initiate and process the refund as soon as is practicable.
        </p>
        <li>
          <strong> Prohibited Activities</strong>
        </li>
        <p>
          6.1 You are solely responsible for compliance with any and all laws,
          rules, regulations, and tax obligations that may apply to your use of
          the Payment Services. In connection with your use of the Payment
          Services, you may not, and you agree that you will not, nor assist or
          enable others to:
        </p>
        <ul>
          <li>a breach or circumvent any applicable laws or regulations;</li>
          <li>
            a breach or circumvent any agreements with third parties,
            third-party rights, or the&nbsp;
            <a href={createAppLink("/policies/terms")}>Terms</a>
            ,&nbsp;Policies, or&nbsp;
            <a href={createAppLink("/policies/community-standards")}>
              Community Standards
            </a>
            ;
          </li>
          <li>
            use the Payment Services for any commercial or other purposes that
            are not expressly permitted by these Payments Terms;
          </li>
          <li>
            register or use any Payment Method or Payout Method with your Teache
            account that is not yours or which you do not have the authorization
            to use;
          </li>
          <li>
            avoid, bypass, remove, deactivate, impair, descramble, or otherwise
            circumvent any technological measure implemented by Teache or any of
            Teache&rsquo; providers or any other third party to protect the
            Payment Services;
          </li>
          <li>
            take any action that damages or adversely affects, or could damage
            or adversely affect, the performance or proper functioning of the
            Payment Services;
          </li>
          <li>
            attempt to decipher, decompile, disassemble, or reverse engineer any
            of the software used to provide the Payment Services; or
          </li>
          <li>
            violate or infringe anyone else&rsquo;s rights or otherwise cause
            harm to anyone.
          </li>
        </ul>
        <p>
          6.2 You may not use or assist others to use the Payment Services to
          send or receive funds (i) into any U.S.-embargoed countries or (ii) to
          anyone on the U.S. Treasury Department&rsquo;s list of Specially
          Designated Nationals or the U.S. Department of Commerce&rsquo;s Denied
          Persons List or Entity List. You represent and warrant that (i)
          neither you nor your Instructor Services are located or take place in
          a country that is subject to a U.S. Government embargo, or the U.S.
          Government has designated that as a &ldquo;terrorist supporting&rdquo;
          country, and that (ii) you are not listed on any U.S. Government list
          of prohibited or restricted parties. In addition to complying with the
          above, you must also comply with any relevant export control laws in
          your local jurisdiction.
        </p>
        <li>
          <strong> Disclaimers</strong>
        </li>
        <p>
          <strong>
            IF YOU CHOOSE TO USE THE TEACHE PLATFORM, YOU DO SO AT YOUR SOLE
            RISK. YOU ACKNOWLEDGE AND AGREE THAT TEACHE DOES NOT HAVE AN
            OBLIGATION TO CONDUCT BACKGROUND CHECKS ON ANY INSTRUCTOR .&nbsp;
            THE TEACHE PLATFORM AND MUTUAL CONTENT ARE PROVIDED &ldquo;AS
            IS&rdquo;, WITHOUT WARRANTY OF ANY KIND, EITHER EXPRESS OR IMPLIED.
            WITHOUT LIMITING THE FOREGOING, TEACHE EXPLICITLY DISCLAIMS ANY
            WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE,
            QUIET ENJOYMENT OR NON-INFRINGEMENT, AND ANY WARRANTIES ARISING OUT
            OF COURSE OF DEALING OR USAGE OF TRADE. TEACHE MAKES NO WARRANTY
            THAT THE TEACHE PLATFORM OR MUTUAL CONTENT, INCLUDING, BUT NOT
            LIMITED TO, THE LISTINGS OR ANY INFORMATION PROVIDED VIA CLASSES
            WILL MEET YOUR REQUIREMENTS OR BE AVAILABLE ON AN UNINTERRUPTED,
            SECURE, OR ERROR-FREE BASIS. TEACHE MAKES NO WARRANTY REGARDING THE
            QUALITY OF ANY LISTINGS, THE SERVICES, THE INSTRUCTOR, OR MUTUAL
            CONTENT OR THE ACCURACY, TIMELINESS, TRUTHFULNESS, COMPLETENESS OR
            RELIABILITY OF ANY MUTUAL CONTENT OBTAINED THROUGH THE TEACHE
            PLATFORM, APPLICATION OR THE SERVICES.
          </strong>
        </p>
        <p>
          <strong>
            NO ADVICE OR INFORMATION, WHETHER ORAL OR WRITTEN, OBTAINED FROM
            TEACHE OR THROUGH THE TEACHE PLATFORM OR MUTUAL CONTENT, WILL CREATE
            ANY WARRANTY NOT EXPRESSLY MADE HEREIN.&nbsp; YOU ARE SOLELY
            RESPONSIBLE FOR ALL OF YOUR COMMUNICATIONS AND INTERACTIONS WITH
            USERS OR INSTRUCTORS, OR OTHER USERS OF THE TEACHE PLATFORM AND WITH
            OTHER PERSONS WITH WHOM YOU COMMUNICATE OR INTERACT AS A RESULT OF
            YOUR USE OF THE TEACHE PLATFORM. YOU UNDERSTAND THAT TEACHE DOES NOT
            MAKE ANY ATTEMPT TO VERIFY USERS ' STATEMENTS OR INSTRUCTORS, OR
            OTHER USERS OF THE TEACHE PLATFORM.&nbsp; TEACHE MAKES NO
            REPRESENTATIONS OR WARRANTIES AS TO THE CONDUCT OF USERS OF THE
            TEACHE PLATFORM.{" "}
          </strong>
        </p>
        <p>
          <strong>
            THE TEACHE PLATFORM MAY CONTAIN ANALYST RATINGS.&nbsp; ANALYST
            RATINGS ARE ESTABLISHED BY USING THE COMPANY&rsquo;S PROPRIETARY
            RATING FORMULA, AT OUR SOLE DISCRETION, WHICH GIVES CONSIDERATION TO
            DIFFERENT CRITERIA, SUCH AS AN INTEGRATOR&rsquo;S WORK HISTORY,
            EDUCATION, PROFILE COMPLETION ON THE TEACHE PLATFORM, AVAILABILITY
            AND USER REVIEWS.&nbsp; THE ANALYST RATINGS ARE OFFERED FOR FREE AND
            "AS IS" WITHOUT REPRESENTATIONS OR WARRANTIES OF ANY KIND.
            &nbsp;WHILE WE HAVE MADE EVERY POSSIBLE EFFORT TO PROVIDE MEANINGFUL
            ANALYST RATINGS, WE CAN NOT GUARANTEE THEIR CORRECTNESS, AND THEY
            MAY BE CHANGED OR REMOVED AT ANY TIME WITHOUT NOTICE, AT THE
            COMPANY&rsquo;S SOLE DISCRETION.&nbsp;{" "}
          </strong>
        </p>
        <li>
          <strong> Liability</strong>
        </li>
        <p>
          <strong>
            8.1 Except as provided in Section 8.2, you acknowledge and agree
            that, to the maximum extent permitted by law, the entire risk
            arising out of your access to and use of the Payment Services
            remains with you. If you permit or authorize another person to use
            your Teache account in any way, you are responsible for the actions
            taken by that person. Neither Teache nor any other party involved in
            creating, producing, or delivering the Payment Services will be
            liable for any incidental, special, exemplary, or consequential
            damages, including lost profits, loss of data or loss of goodwill,
            service interruption, computer damage or system failure or the cost
            of substitute products or services, or for any damages for personal
            or bodily injury or emotional distress arising out of or in
            connection with (i) these Payments Terms, (ii) from the use of or
            inability to use the Payment Services, or (iii) from any
            communications, interactions, or meetings with other Members or
            other persons with whom you communicate, interact, transact, or meet
            with as a result of your use of the Payment Services, whether based
            on warranty, contract, tort (including negligence), product
            liability, or any other legal theory, and whether or not Teache has
            been informed of the possibility of such damage, even if a limited
            remedy set forth herein is found to have failed of its essential
            purpose. Except for our obligations to pay amounts to applicable
            Instructors pursuant to these Payments Terms, in no event will
            Teache aggregate liability arising out of or in connection with
            these Payments Terms and your use of the Payment Services including,
            but not limited to, from your use of or inability to use the Payment
            Services, exceed (i) the amounts you have paid or owe for Class
            bookings via the Teache Platform as a User in the twelve (12) month
            period before the event giving rise to the liability, or if you are
            an Instructor, the amounts paid by Teache to you in the twelve (12)
            month period before the event giving rise to the liability, or (ii)
            one hundred U.S. dollars (US$100), if no such payments have been
            made, as applicable. The limitations of damages set forth above are
            fundamental elements of the basis of the bargain between Teache and
            you. Some jurisdictions do not allow the exclusion or limitation of
            liability for consequential or incidental damages, so the above
            limitation may not apply to you. If you reside outside of the U.S.,
            this does not affect Teache's liability for death or personal injury
            arising from its negligence, nor for fraudulent misrepresentation,
            misrepresentation as to a fundamental matter, or any other liability
            which cannot be excluded or limited under applicable law.
          </strong>
        </p>
        <li>
          <strong> Indemnification</strong>
        </li>
        <p>
          To the maximum extent permitted by applicable law, you agree to
          release, defend (at Teache's option), indemnify, and hold Teache and
          its affiliates and subsidiaries, and their officers, directors,
          employees, and agents, harmless from and against any claims,
          liabilities, damages, losses, and expenses, including, without
          limitation, reasonable legal and accounting fees, arising out of or in
          any way connected with (i) your breach of these Payments Terms; (ii)
          your improper use of the Payment Services; (iii) your failure, or our
          failure at your direction, to accurately report, collect or remit
          taxes; or (iv) your breach of any laws, regulations, or third-party
          rights.
        </p>
        <li>
          <strong> Modification, Term, Termination, and other Measures</strong>
        </li>
        <p>
          <strong>10.1 Modification</strong>. Except as otherwise required by
          applicable law, Teache may modify these Payments Terms at any time. If
          we make material changes to these Payment Terms, we will post the
          revised Payment Terms on the Teache Platform and update the
          &ldquo;Last Updated&rdquo; date at the top of these Payment Terms. If
          you are affected by the modification, we will also notify you at least
          thirty (30) days before the date they become effective.
        </p>
        <p>
          <strong>10.2 Term</strong>. This agreement between you and Teache
          reflected by these Payment Terms is effective when you create a Teache
          account or use the Payment Services; it remains in effect until either
          you or we terminate this agreement in accordance with Section 10.3.
        </p>
        <p>
          <strong>10.3 Termination</strong>. You may terminate this agreement at
          any time by sending us an email or by deleting your Teache account.
          Terminating this agreement will also serve as notice to cancel your
          Teache account pursuant to the&nbsp;
          <a href={createAppLink("/policies/terms")}>Terms</a>. Without limiting
          our rights specified below, Teache may terminate this agreement for
          convenience at any time by giving you thirty (30) days' notice via
          email to your registered email address. Teache may also terminate this
          agreement immediately without notice if (i) you have materially
          breached your obligations under this agreement; (ii) you have provided
          inaccurate, fraudulent, outdated, or incomplete information; (iii) you
          have violated applicable laws, regulations, or third-party rights; or
          (iv) Teache believes in good faith that such action is reasonably
          necessary to protect other Members, Teache, or third parties.
        </p>
        <p>
          <strong>10.4 Suspension and Other Measures</strong>. Teache may limit
          or temporarily or permanently suspend your use of or access to the
          Payment Services (i) to comply with applicable law or the order or
          request of a court, law enforcement, or other administrative agency or
          governmental body, (ii) if you have breached these Payments Terms,
          the&nbsp;<a href={createAppLink("/policies/terms")}>Terms</a>,
          applicable laws, regulations or third-party rights, (iii) if you have
          provided inaccurate, fraudulent, outdated, or incomplete information
          regarding a Payment Method or Payout Method, (iv) for any amounts you
          owe under these Payments Term that are overdue or in default, or (v)
          if Teache believes in good faith that such action is reasonably
          necessary to protect the personal safety or property of Teache, its
          Members, Teache, or third parties, or to prevent fraud or other
          illegal activity. Further, in the event of unsuccessful payment due to
          card expiration, insufficient funds, or otherwise, we may temporarily
          suspend your access to Payment Services, until we can charge a valid
          Payment Method.
        </p>
        <p>
          <strong>10.5 Appeal</strong>. If Teache takes any of the measures
          described in Section 10.3 and 10.4, you may appeal such a decision by
          contacting&nbsp;customer service.
        </p>
        <p>
          <strong>10.6 Effect of Termination</strong>. If you cancel your Teache
          account as an Instructor, or if Teache takes any of the measures
          described above, Teache may provide a full refund to any User with
          confirmed booking(s), and you will not be entitled to any compensation
          for pending or confirmed bookings that were canceled. If you cancel
          your Teache account as a User, Teache will initiate a refund for any
          confirmed booking(s) based on the Listing&rsquo;s cancellation policy.
          Suppose that your access to or use of the Payment Services has been
          suspended or limited, or we have terminated this agreement. In that
          case, you may not register a new Teache account or attempt to access
          and use the Payment Services through a Teache account of another
          Member.
        </p>
        <p>
          <strong>10.7 Survival</strong>. Sections 5 through 20 of these
          Payments Terms shall survive any termination or expiration of this
          agreement.
        </p>
        <li>
          <strong> Governing Law and Dispute Resolution</strong>
        </li>
        <p>
          If you are contracting with Teache US, these Payments Terms will be
          interpreted in accordance with the laws of the State of New York and
          the United States of America, without regard to conflict-of-law
          provisions. Legal proceedings (other than small claims actions) that
          are excluded from the Arbitration Agreement in Section 15 must be
          brought in state or federal court in New York, New York, unless you
          and Teache both agree to some other location. You and Teache both
          consent to venue and personal jurisdiction in New York, New York.
        </p>
        <li>
          <strong> United States Arbitration Agreement</strong>
        </li>
        <p>
          <strong>12.1 Application</strong>. This Arbitration Agreement only
          applies to you if you are contracting with Teache US. Suppose that you
          are not contracting with Teache US, and you nevertheless attempt to
          bring any legal claim against Teache in the United States. In that
          case, this Arbitration Agreement will apply for the determination of
          the threshold issue of whether this Section 12 applies to you and all
          other threshold determinations, including residency, arbitrability,
          venue, and applicable law.
        </p>
        <p>
          <strong>12.2 Overview of Dispute Resolution Process</strong>. Teache
          is committed to participating in a consumer-friendly dispute
          resolution process. To that end, these Payment Terms provide for a
          two-part process for individuals to whom this Section 12 applies: (1)
          an informal negotiation directly with Teache's customer service team
          (described in paragraph 12.3, below), and, if necessary, (2) a binding
          arbitration administered by the American Arbitration Association
          (&ldquo;<strong>AAA</strong>&rdquo;). You and Teache each retain the
          right to seek relief in small claims court as an alternative to
          arbitration.
        </p>
        <p>
          <strong>
            12.3 Mandatory Pre-Arbitration Dispute Resolution and Notification
          </strong>
          . At least 30 days before initiating an arbitration, you and Teache
          each agree to notify the other party of the dispute in writing and
          attempt in good faith to negotiate an informal resolution. You must
          send your notice of dispute to Teache by email to hello@teache.co.
          Teache will send its notice of the dispute to the email address
          associated with your Teache account. A notice of dispute must include
          the party&rsquo;s name and preferred contact information, a brief
          description of the dispute, and the relief sought. If the parties are
          unable to resolve the dispute within the 30-day period, only then may
          either party commence the arbitration by filing a written Demand for
          Arbitration (available at&nbsp www.adr.org) with the AAA and providing
          a copy to the other party, as specified in the AAA Rules (available
          at&nbsp www.adr.org ).
        </p>
        <p>
          <strong>
            12.4 Agreement to Arbitrate. You and Teache mutually agree that any
            dispute, claim, or controversy arising out of or relating to these
            Payments Terms or the applicability, breach, termination, validity,
            enforcement or interpretation thereof, or any use of the Payment
            Services (collectively, &ldquo;
          </strong>
          Disputes
          <strong>
            &rdquo;) will be settled by binding individual arbitration (the
            &ldquo;
          </strong>
          Arbitration Agreement
          <strong>
            &rdquo;). If there is a dispute about whether this Arbitration
            Agreement can be enforced or applies to our Dispute, you and Teache
            agree that the arbitrator will decide that issue.
          </strong>
        </p>
        <p>
          <strong>12.5 Exceptions to Arbitration Agreement</strong>. You and
          Teache each agree that the following causes of action and/or claims
          for relief are exceptions to the Arbitration Agreement and will be
          brought in a judicial proceeding in a court of competent jurisdiction
          (as defined by Section 12): (i) any claim or cause of action seeking
          emergency injunctive relief based on exigent circumstances (e.g.,
          imminent danger or commission of a crime, hacking, cyber-attack); or
          (ii) a request for the remedy of public injunctive relief. You and
          Teache agree that the remedy of public injunctive relief will proceed
          after the arbitration of all arbitrable claims, remedies, or causes of
          action and will be stayed pending the outcome of the arbitration
          pursuant to section 3 of the Federal Arbitration Act.
        </p>
        <p>
          <strong>12.6 Arbitration Rules and Governing Law</strong>. This
          Arbitration Agreement evidences a transaction in interstate commerce,
          and the Federal Arbitration Act governs all substantive and procedural
          interpretation and enforcement of this provision. The arbitration will
          be administered by AAA in accordance with the Consumer Arbitration
          Rules and/or other AAA arbitration rules determined to be applicable
          by the AAA (the &ldquo;<strong>AAA Rules</strong>&rdquo;) then in
          effect, except as modified here. The AAA Rules are available at&nbsp;
          www.adr.org. To initiate arbitration, a completed written demand
          (available at&nbsp; www.adr.org) must be filed with the AAA and
          provided to the other party, as specified in the AAA rules.
        </p>
        <p>
          <strong>
            12.7 Modification to AAA Rules - Arbitration Hearing/Location
          </strong>
          . To make the arbitration most convenient to you, Teache agrees that
          any required arbitration hearing may be conducted at your choice (a)
          in the U.S county where you reside, (b) in New York County, (c) via
          phone or video conference; or (d) if all parties agree, by solely the
          submission of documents to the arbitrator.
        </p>
        <p>
          <strong>
            12.8 Modification of AAA Rules - Attorney&rsquo;s Fees and Costs
          </strong>
          . The AAA Rules shall govern your arbitration fees and your share of
          arbitrator compensation and, where appropriate, limited by the AAA
          Consumer Rules. If such costs are determined by the arbitrator to be
          excessive, Teache will pay all arbitration fees and expenses. Either
          party may make a request that the arbitrator award attorneys&rsquo;
          fees and costs upon proving that the other party has asserted a claim,
          crossclaim or defense that is groundless in fact or law, brought in
          bad faith or for the purpose of harassment, or is otherwise frivolous,
          as allowed by applicable law and the AAA Rules.
        </p>
        <p>
          <strong>12.9 Arbitrator&rsquo;s Decision</strong>. The
          arbitrator&rsquo;s decision will include the essential findings and
          conclusions upon which the arbitrator based the award. Judgment on the
          arbitration award may be entered in any court with proper
          jurisdiction. The arbitrator may award any relief allowed by law or
          the AAA Rules. Declaratory or injunctive relief may be awarded only on
          an individual basis and only to the extent necessary to provide relief
          warranted by the claimant&rsquo;s individual claim.
        </p>
        <p>
          <strong>12.10 Jury Trial Waiver</strong>. You and Teache acknowledge
          and agree that each party waives the right to a trial by jury as to
          all arbitrable Disputes.
        </p>
        <p>
          <strong>12.11 No Class Actions or Representative Proceedings</strong>.
          You and Teache acknowledge and agree that, to the fullest extent
          permitted by law, each party waives the right to participate as a
          plaintiff or class member in any purported class action lawsuit,
          class-wide arbitration, private attorney general action, or any other
          representative or consolidated proceeding. Unless we agree in writing,
          the arbitrator may not consolidate more than one party&rsquo;s claims
          and may not otherwise preside over any form of any class or
          representative proceeding. Suppose that there is a final judicial
          determination that applicable law precludes enforcement of the waiver
          contained in this paragraph as to any claim, cause of action, or
          requested remedy. In that case, that claim, cause of action, or
          requested remedy, and only that claim, cause of action, or requested
          remedy, will be severed from this agreement to arbitrate and will be
          brought in a court of competent jurisdiction. In the event that a
          claim, cause of action, or requested remedy is severed pursuant to
          this paragraph, then you and we agree that the claims, causes of
          action, or requested remedies that are not subject to arbitration will
          be stayed until all arbitrable claims, causes of action, and requested
          remedies are resolved by the arbitrator.
        </p>
        <p>
          <strong>12.12 Severability</strong>. Except as provided in Section
          12.11, in the event that any portion of this Arbitration Agreement is
          deemed illegal or unenforceable, such provision shall be severed and
          the remainder of the Arbitration Agreement will be given full force
          and effect.
        </p>
        <p>
          <strong>12.13 Changes to Agreement to Arbitrate</strong>. Suppose that
          Teache changes this Section 12 after the date you last accepted these
          Payments Terms (or accepted any subsequent changes to these Payments
          Terms). In that case, you may reject that change by sending us written
          notice (including by email) within thirty (30) days of the date the
          change is effective. Rejecting a new change, however, does not revoke
          or alter your prior consent to any earlier agreements to arbitrate any
          Dispute between you and Teache (or your prior consent to any
          subsequent changes thereto), which will remain in effect and
          enforceable as to any Dispute between you and Teache.
        </p>
        <p>
          <strong>12.14 Survival</strong>. Except as provided in Section 12.12
          and subject to Section 10.7, this Section 12 will survive any
          termination of these Payments Terms. It will continue to apply, even
          if you stop using the Payment Services or terminate your Teache
          account.
        </p>
        <li>
          <strong> Miscellaneous</strong>
        </li>
        <p>
          <strong>13.1 Interpreting these Payment Terms</strong>. Except as
          additional terms and conditions may supplement these Payments Terms
          constitute the entire agreement between you and Teache regarding the
          subject matter hereof and supersede any and all prior oral or written
          understandings or agreements between Teache and you regarding the
          Payment Services. If any provision of these Payments Terms is held to
          be invalid or unenforceable, such provision will be struck. It will
          not affect the validity and enforceability of the remaining
          provisions.
        </p>
        <p>
          <strong>13.2 No Waiver</strong>. Teache's failure to enforce any right
          or provision in these Payments Terms will not constitute a waiver of
          such right or provision, unless acknowledged and agreed to by us in
          writing. Except as expressly outlined in these Payments Terms, the
          exercise by either party of any of its remedies under these Payments
          Terms will be without prejudice to its other remedies under these
          Payments Terms or otherwise permitted under law.
        </p>
        <p>
          <strong>13.3 Assignment</strong>. You may not assign, transfer, or
          delegate this agreement or your rights and obligations hereunder
          without Teache's prior written consent. Teache may, without
          restriction, assign, transfer, or delegate this agreement and any
          rights and obligations, at its sole discretion, with thirty (30) days
          prior notice (or two (2) months prior notice for Members contracting
          with Teache).
        </p>
        <p>
          <strong>13.4 Notices</strong>. Unless specified otherwise, any notices
          or other communications permitted or required under this agreement
          will be in writing and will be given by Teache via email or via a
          notification on the Teache Platform, depending on your notification
          settings and/or messaging service (including SMS and WeChat). The date
          of receipt will be deemed the date on which Teache transmits the
          notice.
        </p>
        <li>
          <strong> Additional Clauses for Users that are Businesses</strong>
        </li>
        <p>
          The following paragraphs also apply if you are using the Payment
          Services as a representative (&ldquo;<strong>Representative</strong>
          &rdquo;) acting on behalf of a business, company, or other legal
          entity (in such event, for purposes of the Payments Terms,
          &ldquo;you&rdquo; and &ldquo;your&rdquo; will refer and apply to that
          business, company, or other legal entity).
        </p>
        <p>
          14.1 You accept the Payment Terms and will be responsible for any act
          or omission of employees or third-party agents using the Payment
          Service on your behalf.
        </p>
        <p>
          14.2 You and your Representative individually affirm that you are
          authorized to provide the information described in Section 2.1 and
          Section 3.2 and that your Representative has the authority to bind you
          to these Payments Terms. We may require you to provide additional
          information or documentation demonstrating your Representative&rsquo;s
          authority.
        </p>
        <p>
          14.3 You represent and warrant to us that (i) you are duly organized,
          validly existing and in good standing under the laws of the country in
          which your business is registered and that you are registering for
          receiving the Payment Services and that (ii) you have all requisite
          right, power, and authority to enter into this agreement, perform your
          obligations, and grant the rights, licenses, and authorizations in
          this agreement.
        </p>
        <p>
          14.4 If you are using your Payment Method for the benefit of your
          employees or other authorized third parties in connection with Teache
          for Work, as permitted by your account, you authorize Teache to charge
          your Payment Method for bookings requested by employees at your
          company or other permitted third party.
        </p>
        <p>
          14.5 For any Payout Method linked to your Teache account, you
          authorize Teache to store the Payout Method, remit payments using the
          Payout Method for bookings associated with your Teache account, and
          take any other action, as permitted in the Payments Terms in respect
          of the Payout Method.
        </p>
        <p>
          14.6 If you handle, store, or otherwise process payment card
          information on behalf of anyone or any third party, you agree to
          comply on an ongoing basis with applicable data privacy and security
          requirements under the current Payment Card Industry Data Security
          Standard with regards to the use, access, and storage of such credit
          card information. For additional information, including tools to help
          you assess your compliance, see&nbsp; https://www.visa.com/cisp
          &nbsp;and&nbsp; https://www.mastercard.com/sdp.
        </p>
        <li>
          <strong> Contacting Teache</strong>
        </li>
      </ol>
      <p>
        You may contact Teache regarding the Payment Services using the
        information below:
      </p>
      <p>
        Teache, Inc.
        <br />
        79 Madison Ave.
        <br />
        New York, NY 10016
        <br />
        United States of America
        <br />
        hello@teache.co
      </p>
    </>
  )
}

export default PaymentTerms
